<template>
  <div>
    <v-row>
      <v-col cols="12" v-if="!includedInPlan('sites_builder')">
        <v-alert type="info" outlined dense>
          {{ $t('sites_builder_tokens.feature_not_included') }}
        </v-alert>
      </v-col>

      <v-col cols="12" v-else>
        <p class="text-body-2 grey--text text--darken-1 mb-3">
          {{ $t('sites_builder_tokens.description') }}
        </p>

        <v-btn small depressed color="primary" @click="openCreateDialog" :loading="isLoading" class="mb-3">
          <v-icon left small>mdi-plus</v-icon>
          {{ $t('sites_builder_tokens.create_button') }}
        </v-btn>

        <v-data-table
          :headers="headers"
          :items="tokens"
          :loading="isLoading"
          :no-data-text="$t('sites_builder_tokens.no_tokens')"
          dense
          hide-default-footer
          :items-per-page="-1"
        >
          <template v-slot:item.last4="{ item }">
            <code>...{{ item.last4 }}</code>
          </template>
          <template v-slot:item.level="{ item }">
            <v-chip x-small label>{{ $t('sites_builder_tokens.level_' + item.level) }}</v-chip>
          </template>
          <template v-slot:item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>
          <template v-slot:item.last_used_at="{ item }">
            {{ item.last_used_at ? formatDate(item.last_used_at) : '—' }}
          </template>
          <template v-slot:item.revoked_at="{ item }">
            <v-chip x-small color="error" v-if="item.revoked_at">{{ $t('sites_builder_tokens.revoked') }}</v-chip>
            <v-chip x-small color="success" v-else>{{ $t('sites_builder_tokens.active') }}</v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn x-small icon color="error" v-if="!item.revoked_at"
              @click="confirmRevoke(item)" :disabled="isLoading">
              <v-icon small>mdi-delete-outline</v-icon>
            </v-btn>
          </template>
        </v-data-table>

        <!-- Create Dialog -->
        <v-dialog v-model="createDialog" max-width="500" persistent>
          <v-card>
            <v-card-title>{{ $t('sites_builder_tokens.create_title') }}</v-card-title>
            <v-card-text>
              <v-text-field v-model="newTokenName" :label="$t('sites_builder_tokens.name_label')"
                outlined dense :rules="[v => !!v || $t('sites_builder_tokens.name_required')]" autofocus />
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="createDialog = false" :disabled="isCreating">{{ $t('cancel') }}</v-btn>
              <v-btn color="primary" depressed :loading="isCreating" :disabled="!newTokenName"
                @click="createToken">{{ $t('create') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Plain token displayed ONCE -->
        <v-dialog v-model="plainDialog" max-width="600" persistent>
          <v-card>
            <v-card-title>{{ $t('sites_builder_tokens.plain_title') }}</v-card-title>
            <v-card-text>
              <v-alert type="warning" outlined dense class="mb-3">
                {{ $t('sites_builder_tokens.plain_warning') }}
              </v-alert>
              <v-text-field :value="plainTokenShown" readonly outlined dense
                autocomplete="off" spellcheck="false"
                append-icon="mdi-content-copy" @click:append="copyToken" />
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn color="primary" depressed @click="plainDialog = false">
                {{ $t('sites_builder_tokens.plain_close') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Revoke confirm -->
        <v-dialog v-model="revokeDialog" max-width="400">
          <v-card>
            <v-card-title>{{ $t('sites_builder_tokens.revoke_title') }}</v-card-title>
            <v-card-text>
              {{ $t('sites_builder_tokens.revoke_confirm', { name: tokenToRevoke && tokenToRevoke.name }) }}
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="revokeDialog = false">{{ $t('cancel') }}</v-btn>
              <v-btn color="error" depressed :loading="isLoading" @click="revokeToken">
                {{ $t('sites_builder_tokens.revoke_button') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

export default {
  name: 'SitesBuilderTokens',
  data() {
    return {
      tokens: [],
      isLoading: false,
      isCreating: false,
      createDialog: false,
      plainDialog: false,
      revokeDialog: false,
      newTokenName: '',
      plainTokenShown: '',
      tokenToRevoke: null,
    };
  },
  watch: {
    plainDialog(visible) {
      if (!visible) {
        this.plainTokenShown = '';
      }
    },
  },
  computed: {
    headers() {
      return [
        { text: this.$t('sites_builder_tokens.name'), value: 'name' },
        { text: this.$t('sites_builder_tokens.last4'), value: 'last4', sortable: false },
        { text: this.$t('sites_builder_tokens.level'), value: 'level' },
        { text: this.$t('sites_builder_tokens.status'), value: 'revoked_at', sortable: false },
        { text: this.$t('sites_builder_tokens.created_at'), value: 'created_at' },
        { text: this.$t('sites_builder_tokens.last_used_at'), value: 'last_used_at' },
        { text: '', value: 'actions', sortable: false },
      ];
    },
  },
  mounted() {
    if (this.includedInPlan('sites_builder')) {
      this.loadTokens();
    }
  },
  methods: {
    // Strict mode: server-side `Plan#sites_builder_enabled?` does NOT honour trial.
    // Don't show UI controls during trial unless plan explicitly includes flag —
    // otherwise user gets a 403 immediately on POST /api/v1/sites_api_tokens.
    includedInPlan(service) {
      const mc = this.$store.getters.getMainCompany;
      if (mc && mc.plan && mc.plan.included_services) {
        return mc.plan.included_services.includes(service);
      }
      return false;
    },
    formatDate(iso) {
      return moment(iso).format('YYYY-MM-DD HH:mm');
    },
    async loadTokens() {
      this.isLoading = true;
      try {
        const { data } = await axios.get('/api/v1/sites_api_tokens');
        this.tokens = data.tokens || [];
      } catch (err) {
        this.$notify(this.$t('sites_builder_tokens.load_error'), '', 'error');
      } finally {
        this.isLoading = false;
      }
    },
    openCreateDialog() {
      this.newTokenName = '';
      this.createDialog = true;
    },
    async createToken() {
      if (!this.newTokenName) return;
      this.isCreating = true;
      try {
        const { data } = await axios.post('/api/v1/sites_api_tokens', {
          name: this.newTokenName,
          level: 'main_company',
        });
        this.tokens.unshift(data.token);
        this.plainTokenShown = data.plain_token;
        this.createDialog = false;
        this.plainDialog = true;
      } catch (err) {
        this.$notify(this.$t('sites_builder_tokens.create_error'), '', 'error');
      } finally {
        this.isCreating = false;
      }
    },
    confirmRevoke(token) {
      this.tokenToRevoke = token;
      this.revokeDialog = true;
    },
    async revokeToken() {
      if (!this.tokenToRevoke) return;
      this.isLoading = true;
      try {
        await axios.delete(`/api/v1/sites_api_tokens/${this.tokenToRevoke.id}`);
        await this.loadTokens();
        this.revokeDialog = false;
        this.$notify(this.$t('sites_builder_tokens.revoked_success'), '', 'success');
      } catch (err) {
        this.$notify(this.$t('sites_builder_tokens.revoke_error'), '', 'error');
      } finally {
        this.isLoading = false;
        this.tokenToRevoke = null;
      }
    },
    async copyToken() {
      try {
        await navigator.clipboard.writeText(this.plainTokenShown);
        this.$notify(this.$t('sites_builder_tokens.copied'), '', 'success');
      } catch (err) {
        this.$notify(this.$t('sites_builder_tokens.copy_failed'), '', 'warning');
      }
    },
  },
};
</script>
